import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Chip, IconButton, Typography, Avatar,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PublishIcon from '@mui/icons-material/Publish';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ReactComponent as BackArrow } from '../../assets/icons/back-arrow.svg';
import GalleryTopBar from '../../components/GalleryTopBar';
import { useNewspaperStore } from '../../hooks/zustand/newspapers';
import * as NewspapersAPI from '../../api/Newspapers';
import * as PublishedEditionsAPI from '../../api/PublishedEditions';
import { track, Events } from '../../utils/analytics';
import confetti from 'canvas-confetti';
import { Edition, Story, STORY_TEMPLATE_LABELS, StoryTemplate } from '../../types/models/Newspaper';
import * as ClassroomsAPI from '../../api/Classrooms';

const STATUS_COLOR: Record<string, string> = {
  draft: '#f57c00',
  open: '#2e7d32',
  published: '#1565c0',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  open: 'Assigned',
  published: 'Published',
};

interface Student {
  id: string;
  name: string;
  surname: string;
}

export default function EditionPage() {
  const { id: newspaperId, editionId } = useParams<{ id: string; editionId: string }>();
  const navigate = useNavigate();

  const currentNewspaper = useNewspaperStore((state) => state.currentNewspaper);
  const currentStories = useNewspaperStore((state) => state.currentStories);
  const loadStories = useNewspaperStore((state) => state.loadStories);
  const setCurrentNewspaper = useNewspaperStore((state) => state.setCurrentNewspaper);

  const [edition, setEdition] = useState<Edition | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!newspaperId || !editionId) return;

    NewspapersAPI.getById(newspaperId).then((res) => {
      const newspaper = res.data.data;
      setCurrentNewspaper(newspaper);

      if (newspaper.classrooms?.length) {
        const classroomId = newspaper.classrooms[0].id;
        ClassroomsAPI.getClassroomData(classroomId).then((res2: any) => {
          const data = res2.data?.data ?? res2.data;
          setStudents(data?.students ?? []);
        }).catch(() => {});
      }
    }).catch(() => {});

    NewspapersAPI.getEditions(newspaperId).then((res) => {
      const found = res.data.data.find((e: Edition) => e.id === editionId);
      if (found) setEdition(found);
    }).catch(() => {});

    loadStories(newspaperId, editionId);
  }, [newspaperId, editionId]);

  const handleAssign = async () => {
    if (!newspaperId || !editionId) return;
    setIsAssigning(true);
    try {
      await NewspapersAPI.updateEdition(newspaperId, editionId, { status: 'open' });
      setEdition((prev) => prev ? { ...prev, status: 'open' } : prev);
      setShowAssignDialog(false);
    } finally {
      setIsAssigning(false);
    }
  };

  const handlePublish = async () => {
    if (!newspaperId || !editionId) return;
    setIsPublishing(true);
    try {
      const res = await PublishedEditionsAPI.publish(newspaperId, editionId, {
        visibility: 'public',
      });
      const slug = (res.data as any).data?.slug;
      setPublishedSlug(slug);
      setEdition((prev) => prev ? { ...prev, status: 'published' } : prev);
      track(Events.PublishEdition, {
        newspaper_id: newspaperId,
        edition_id: editionId,
      });
      confetti({ particleCount: 200, spread: 100, origin: { y: 0.5 } });
    } finally {
      setIsPublishing(false);
    }
  };

  const fullShareUrl = publishedSlug ? `${window.location.origin}/read/${publishedSlug}` : null;

  window.document.title = edition?.name ?? 'Edition';

  const storyByStudentId = (studentId: string): Story | undefined =>
    currentStories.find((s) => s.student?.id === studentId);

  const assignedCount = currentStories.length;
  const totalStudents = students.length;
  const isOpen = edition?.status === 'open' || edition?.status === 'published';

  return (
    <>
      <GalleryTopBar tab="newspapers" />
      <div style={{ background: '#F0F0F4', minHeight: 'calc(100vh - 64px)', padding: '2rem' }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <IconButton onClick={() => navigate(`/newspaper/${newspaperId}`)} size="small">
            <BackArrow height={20} width={20} stroke="#163567" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {currentNewspaper?.name}
          </Typography>
        </Box>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" fontWeight={700} color="#163567">
              {edition?.name ?? '…'}
            </Typography>
            {edition && (
              <Chip
                label={STATUS_LABEL[edition.status] ?? edition.status}
                size="small"
                sx={{
                  color: '#fff',
                  background: STATUS_COLOR[edition.status] ?? '#888',
                  fontWeight: 600,
                }}
              />
            )}
            {edition?.publish_date && (
              <Typography variant="body2" color="text.secondary">
                {edition.publish_date}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {edition?.status === 'draft' && (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setShowAssignDialog(true)}
                sx={{ background: '#2e7d32', '&:hover': { background: '#1b5e20' } }}
              >
                Assign to students
              </Button>
            )}
            {edition?.status === 'open' && (
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => setShowPublishDialog(true)}
                sx={{ background: '#1565c0', '&:hover': { background: '#0d47a1' } }}
              >
                Publish newspaper
              </Button>
            )}
          </Box>
        </Box>

        {/* Status banner */}
        {isOpen && (
          <Box sx={{
            background: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box>
              <Typography variant="body2" fontWeight={600} color="#2e7d32">
                This edition is live! Students can pick their stories.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {assignedCount} of {totalStudents} students have picked a story
              </Typography>
            </Box>
          </Box>
        )}

        {edition?.status === 'published' && (
          <Box sx={{
            background: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box>
              <Typography variant="body2" fontWeight={600} color="#1565c0">
                This newspaper is published!
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowPublishDialog(true)}
              sx={{ borderColor: '#1565c0', color: '#1565c0' }}
            >
              Share link
            </Button>
          </Box>
        )}

        {edition?.status === 'draft' && (
          <Box sx={{
            background: '#fff3e0',
            border: '1px solid #ffcc80',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}>
            <Typography variant="body2" fontWeight={600} color="#e65100">
              This edition is still a draft.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click &ldquo;Assign to students&rdquo; to let {totalStudents > 0 ? `your ${totalStudents} students` : 'students'} choose their story type.
            </Typography>
          </Box>
        )}

        {/* Student roster */}
        {students.length > 0 ? (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {totalStudents} student{totalStudents !== 1 ? 's' : ''} in {currentNewspaper?.classrooms?.[0]?.name ?? 'this class'}
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 1.5,
            }}>
              {students.map((student) => {
                const story = storyByStudentId(student.id);
                const hasDocument = !!story?.document_id;
                const storyStatus = story?.status as string | undefined;

                const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
                  draft: { label: 'Writing', color: '#888', bg: '#f5f5f5' },
                  submitted: { label: 'Turned in', color: '#e65100', bg: '#fff3e0' },
                  published: { label: 'Published', color: '#1565c0', bg: '#e3f2fd' },
                };
                const status = storyStatus ? statusConfig[storyStatus] : null;

                return (
                  <Box
                    key={student.id}
                    onClick={() => {
                      if (hasDocument) navigate(`/document/${story!.document_id}/`);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      background: '#fff',
                      borderRadius: 2,
                      border: storyStatus === 'submitted'
                        ? '2px solid #e65100'
                        : story ? '1px solid #a5d6a7' : '1px solid #e0e0e0',
                      cursor: hasDocument ? 'pointer' : 'default',
                      transition: 'all 0.15s ease',
                      '&:hover': hasDocument ? {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        transform: 'translateY(-1px)',
                      } : {},
                    }}
                  >
                    <Avatar sx={{
                      width: 36,
                      height: 36,
                      background: storyStatus === 'submitted' ? '#e65100' : story ? '#2e7d32' : '#163567',
                      fontSize: '0.85rem',
                    }}>
                      {student.name[0]}{student.surname[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {student.name} {student.surname}
                      </Typography>
                      {story ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Chip
                            label={STORY_TEMPLATE_LABELS[story.template_type as StoryTemplate] ?? story.template_type}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 18 }}
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                      ) : (
                        <Typography variant="caption" color={isOpen ? 'text.secondary' : 'text.disabled'}>
                          {isOpen ? 'Waiting to pick...' : 'Not yet assigned'}
                        </Typography>
                      )}
                    </Box>
                    {/* Status badge */}
                    {status && (
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{
                          fontSize: '0.6rem',
                          height: 20,
                          fontWeight: 700,
                          color: status.color,
                          background: status.bg,
                          border: `1px solid ${status.color}`,
                        }}
                      />
                    )}
                    {/* Click hint for documents */}
                    {hasDocument && (
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        View &rarr;
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <Typography variant="body1">
              No students found in this classroom yet.
            </Typography>
          </Box>
        )}
      </div>

      {/* Assign confirmation dialog */}
      <Dialog open={showAssignDialog} onClose={() => setShowAssignDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign this edition?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will let students in <strong>{currentNewspaper?.classrooms?.map((c) => c.name).join(', ')}</strong> see this edition and pick their own story type.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each student will choose from templates like News Story, Book Review, Restaurant Review, and more.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={isAssigning}
            sx={{ background: '#2e7d32' }}
          >
            {isAssigning ? 'Assigning...' : 'Assign to students'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish dialog */}
      <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {publishedSlug ? 'Your newspaper is live!' : 'Publish this edition?'}
        </DialogTitle>
        <DialogContent>
          {!publishedSlug ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This will compile all submitted stories into a shareable newspaper that students and families can read.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentStories.filter((s) => (s.status as string) === 'submitted' || (s.status as string) === 'published').length} stories will be included.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Share this link with students and families:
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                background: '#f5f5f5',
                borderRadius: 1,
                mb: 2,
              }}>
                <Typography variant="body1" fontWeight={600} sx={{ flex: 1, fontFamily: 'monospace' }}>
                  {fullShareUrl}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(fullShareUrl!);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.open(`/read/${publishedSlug}`, '_blank')}
              >
                Preview newspaper &rarr;
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!publishedSlug ? (
            <>
              <Button onClick={() => setShowPublishDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handlePublish}
                disabled={isPublishing}
                sx={{ background: '#1565c0' }}
              >
                {isPublishing ? 'Publishing...' : 'Publish newspaper'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setShowPublishDialog(false)}>Done</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
